import styled, { css } from "styled-components"
import { zeroLineHeight, wrapWhenNarrow } from '../styles/mixins'
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDetectClickOutside } from "react-detect-click-outside"
import Icon from "../Icon"
import { BREAKPOINTS, TAP_MIN, Z } from "../GlobalTheme"
import { InsideModalContext } from "./modalContext"

const LIST_GAP = 8
const LIST_MAX_HEIGHT = 200
const VIEWPORT_MARGIN = 12

const Container = styled.div<{ $width: string }>`
  width: ${props => props.$width};
  position: relative;
`

const TitleContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  padding: 12px;
  display: flex;
  position: relative;
  cursor: pointer;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    padding-right: 34px;
    min-height: ${TAP_MIN}px;
    align-items: center;
  }
`

const Label = styled.p<{ $isIcon: boolean }>`
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  ${zeroLineHeight}
  margin: 0px;
  margin-top: 4px;
  margin-left: ${props => props.$isIcon ? '12px' : '0px'};
`

const Value = styled.p<{ $isIcon: boolean, $isLabelles: boolean }>`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_900};
  ${zeroLineHeight}
  margin: 0px;
  margin-top: ${props => props.$isLabelles ? '0px' : '16px'};
  margin-left: ${props => props.$isIcon ? '12px' : '0px'};
  ${wrapWhenNarrow}
`

const ArrowContainer = styled.div<{ $isOpen: boolean, $isLabelles: boolean }>`
  width: 16px;
  height: 16px;
  position: absolute;
  right: 12px;
  top: ${props => props.$isLabelles ? '4px' : '16px'};
  transform: rotate(${props => props.$isOpen ? '180deg' : '0deg'});
`

const OptionsContainer = styled.div<{ $width: string, $isAbsolute: boolean, $fitContent: boolean, $isFloating: boolean }>`
  position: ${props => props.$isFloating ? 'fixed' : props.$isAbsolute ? 'absolute' : 'relative'};
  /* $fitContent sizes the panel to its longest option instead of to the closed box. Opt-in, because
     most callers sit in a modal column where a content-width panel would overflow the modal. The
     panel is absolutely positioned for those callers, so growing it moves nothing else.
     max-content is measured unwrapped, so the options stop breaking mid-filename; past max-width
     they wrap again exactly as before, which keeps this free of horizontal scrolling. */
  width: ${props => props.$fitContent ? 'max-content' : `calc(${props.$width} + 24px)`};
  ${props => props.$fitContent && css`
    min-width: calc(${props.$width} + 24px);
    max-width: min(460px, calc(100vw - 32px));
  `}

  @media (max-width: ${BREAKPOINTS.shell}px) {
    left: 0px;
    right: auto;
    width: 100%;
    /* The floor above is in px, so on a phone it would hold the panel wider than the screen. */
    min-width: 0px;
    max-width: calc(100vw - 24px);
    box-sizing: border-box;
  }
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.gray_300};
  margin-top: ${props => props.$isFloating ? '0px' : `${LIST_GAP}px`};
  background-color: ${props => props.theme.colors.white};
  z-index: ${props => props.$isFloating ? Z.modalDropdown : 1000};
  max-height: 200px;
  overflow-y: auto;
`

const OptionLabel = styled.p`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_900};
  margin: 16px;
  cursor: pointer;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    margin: 0px;
    padding: 14px 16px;
    font-size: 14px;
    display: flex;
    align-items: center;
    min-height: ${TAP_MIN}px;
  }

  &:hover {
    margin-left: 18px;
  }
  overflow-wrap: anywhere;
`

export default function Select({ width, icon, label, options, handleSelect, isAbsolute, fitOptionsToContent }: { width: string, icon?: string, label?: string, options: string[], handleSelect: (value: string) => void, isAbsolute?: boolean, fitOptionsToContent?: boolean }) {

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [selected, setSelected] = useState<string>(options[0])
  const [floatingPosition, setFloatingPosition] = useState<React.CSSProperties | null>(null)
  const titleRef = useRef<HTMLDivElement | null>(null)
  const optionsRef = useRef<HTMLDivElement | null>(null)
  const isInsideModal = useContext(InsideModalContext)
  const containerRef = useDetectClickOutside({ onTriggered: e => { if (!optionsRef.current?.contains(e.target as Node)) setIsOpen(false) } })

  /* Below the shell breakpoint a modal scrolls its own content, and a scroll container clips an
     absolutely positioned list at the modal's edge. There the list is rendered into <body> with fixed
     positioning instead, under the box, or above it when the viewport has more room there. */
  const isFloating = isOpen && !!isAbsolute && isInsideModal && window.matchMedia(`(max-width: ${BREAKPOINTS.shell}px)`).matches

  // Send the first element of options as selected to prevent value from being undefined if user does not change the default option
  // isPreventAutoSelect prevents this auto calling, used in places where its unneccesary
  useEffect(() => {
    handleSelect(options[0])
  }, [])

  useLayoutEffect(() => {
    if (!isFloating) return

    function place() {
      const title = titleRef.current
      const list = optionsRef.current
      if (!title || !list) return
      const box = title.getBoundingClientRect()
      const listHeight = Math.min(LIST_MAX_HEIGHT, list.scrollHeight + 2)
      const spaceBelow = window.innerHeight - box.bottom - LIST_GAP - VIEWPORT_MARGIN
      const spaceAbove = box.top - LIST_GAP - VIEWPORT_MARGIN
      const opensUp = spaceBelow < listHeight && spaceAbove > spaceBelow
      setFloatingPosition({
        left: box.left,
        width: box.width,
        ...(opensUp ? { bottom: window.innerHeight - box.top + LIST_GAP } : { top: box.bottom + LIST_GAP }),
        maxHeight: Math.min(LIST_MAX_HEIGHT, opensUp ? spaceAbove : spaceBelow),
      })
    }

    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [isFloating])

  function handleClick(event: React.MouseEvent<HTMLElement>, value: string) {
    event.stopPropagation()
    setSelected(value)
    handleSelect(value)
    setIsOpen(false)
  }

  const optionList = (
    <OptionsContainer ref={optionsRef} $isAbsolute={isAbsolute || false} $fitContent={fitOptionsToContent || false} $isFloating={isFloating} $width={width}
      style={isFloating ? floatingPosition ?? { visibility: 'hidden' } : undefined}>
      {options && [...new Set(options)].map(option => (
        <OptionLabel key={option} onClick={(e) => handleClick(e, option)}>{option}</OptionLabel>
      ))}
    </OptionsContainer>
  )

  return (
    <>
      <Container $width={width} ref={containerRef}>
        <TitleContainer ref={titleRef} onClick={(e) => {e.stopPropagation(); setIsOpen(!isOpen)}}>
          {icon && (
            <Icon path={icon} width="24px" height="24px" />
          )}
          <div>
            {label && (
              <Label $isIcon={icon ? true : false}>{label}</Label>
            )}
            <Value $isLabelles={!label} $isIcon={icon ? true : false}>{selected}</Value>
          </div>
          <ArrowContainer $isLabelles={!label} $isOpen={isOpen}>
            <Icon path="general.downArrow" width="100%" height="100%" />
          </ArrowContainer>
        </TitleContainer>
        {isOpen && !isFloating && optionList}
        {isFloating && createPortal(optionList, document.body)}
      </Container>
    </>
  )
}